import { buildStaticRuleSetDisplays } from '@fruitful/conversational-mm';
import {
    type AhaMomentResult,
    buildInvestmentWaterfall,
    computeAhaMoment,
    computeDisplayHeadline,
    initIntakeInvestment,
    type InvestmentWaterfallResult,
} from '@fruitful/money-map-simulator';
import {
    type AhaMomentIntake,
    type AhaMomentWidgetProps,
    CMMBranch,
} from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { Linking, Platform, TextInput } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteEmphasized,
    H5Emphasized,
    LabelEmphasized,
    LabelRegular,
    ScreenContainer,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import storage from '../../../common/providers/StorageFacade';
import { BRANCH_PRESETS } from '../../../llm-service/dev/cmmScenarios';
import { onboardingChatResultsAtom } from '../../../llm-service/state/OnboardingChatAtoms';

/* ─── Types ─── */

interface SimulationDebug {
    monthlyGoalsCents: number;
    accounts: Array<{
        name: string;
        balance: string;
        rate?: string;
        minPayment?: string;
        excluded?: boolean;
    }>;
    ruleSets: Array<{
        name: string;
        rules: Array<{ description: string; splitPct: string; type: string }>;
    }>;
    moneyMapJson: string;
    waterfall?: InvestmentWaterfallResult;
}

type AhaOutput = AhaMomentWidgetProps & {
    simulationDebug?: SimulationDebug;
    headlineType: string;
    displayHeadline: string;
};

/* ─── Branch metadata ─── */

interface BranchInfo {
    branch: CMMBranch;
    label: string;
    implemented: boolean;
}

interface BranchGroup {
    welcomeOption: string;
    branches: BranchInfo[];
}

const BRANCH_GROUPS: BranchGroup[] = [
    {
        welcomeOption: 'Get out of debt',
        branches: [
            {
                branch: CMMBranch.GoalsDebt,
                label: 'Mixed debt',
                implemented: true,
            },
            {
                branch: 'GoalsDebt_LowInterest' as CMMBranch,
                label: 'Low-interest only',
                implemented: true,
            },
            {
                branch: 'GoalsDebt_MortgageOnly' as CMMBranch,
                label: 'Mortgage only',
                implemented: true,
            },
            {
                branch: 'GoalsDebt_Overwhelming' as CMMBranch,
                label: 'Overwhelming',
                implemented: true,
            },
            {
                branch: 'GoalsDebt_Overwhelming2' as CMMBranch,
                label: 'Overwhelming 2',
                implemented: true,
            },
        ],
    },
    {
        welcomeOption: 'Save for something',
        branches: [
            {
                branch: CMMBranch.GoalsSavings,
                label: 'No debt',
                implemented: true,
            },
            {
                branch: 'GoalsSavings_WithDebt' as CMMBranch,
                label: 'With high-interest debt',
                implemented: true,
            },
            {
                branch: 'GoalsSavings_WithLowDebt' as CMMBranch,
                label: 'With low-interest debt',
                implemented: true,
            },
            {
                branch: 'GoalsSavings_WithMixedDebt' as CMMBranch,
                label: 'With mixed debt',
                implemented: true,
            },
            {
                branch: 'GoalsSavings_Overwhelming' as CMMBranch,
                label: 'Overwhelming (negative money)',
                implemented: true,
            },
        ],
    },
    {
        welcomeOption: 'Create a money system',
        branches: [
            {
                branch: CMMBranch.MoneySystem,
                label: 'Money system',
                implemented: true,
            },
        ],
    },
    {
        welcomeOption: 'Optimize my investments',
        branches: [
            {
                branch: CMMBranch.StartInvesting,
                label: 'Solo',
                implemented: true,
            },
            {
                branch: 'StartInvesting_FullAccounts' as CMMBranch,
                label: 'Solo — Traditional/Roth/401k/brokerage',
                implemented: true,
            },
            {
                branch: 'StartInvesting_Joint' as CMMBranch,
                label: 'Joint',
                implemented: true,
            },
            {
                branch: CMMBranch.GoalsFire,
                label: 'MFS',
                implemented: true,
            },
            {
                branch: 'StartInvesting_Age50CatchUp' as CMMBranch,
                label: 'Joint age 51 (IRA catch-up)',
                implemented: true,
            },
            {
                branch: 'StartInvesting_TradIraExclusion' as CMMBranch,
                label: 'Solo — funded trad IRA (excluded)',
                implemented: true,
            },
            {
                branch: 'StartInvesting_JointBothTradIra' as CMMBranch,
                label: 'Joint — both funded trad IRAs (excluded)',
                implemented: true,
            },
        ],
    },
];

/* ─── Intake validation ─── */

function validateIntakeConsistency(intake: AhaMomentIntake): string[] {
    const warnings: string[] = [];

    const isInvestBranch = intake.branch === CMMBranch.StartInvesting;

    const {
        monthlyTakeHomeCents,
        monthlyExpensesCents,
        monthlySpendCents,
        monthlyGoalsSpendCents,
    } = intake;

    if (!isInvestBranch) {
        const bucketSum =
            monthlyExpensesCents + monthlySpendCents + monthlyGoalsSpendCents;
        if (bucketSum !== monthlyTakeHomeCents) {
            warnings.push(
                `Bucket mismatch: bills ($${(monthlyExpensesCents / 100).toLocaleString()}) + spend ($${(monthlySpendCents / 100).toLocaleString()}) + goals ($${(monthlyGoalsSpendCents / 100).toLocaleString()}) = $${(bucketSum / 100).toLocaleString()}, but income is $${(monthlyTakeHomeCents / 100).toLocaleString()}`,
            );
        }
    }

    if (intake.bills != null && intake.bills.length > 0) {
        const billsSum = intake.bills.reduce((s, b) => s + b.amountCents, 0);
        if (billsSum !== monthlyExpensesCents) {
            warnings.push(
                `Itemized bills sum ($${(billsSum / 100).toLocaleString()}) ≠ monthlyExpensesCents ($${(monthlyExpensesCents / 100).toLocaleString()})`,
            );
        }
    }

    if (intake.debts != null && intake.bills != null) {
        const debtMinFromDebts = intake.debts.reduce(
            (s, d) => s + d.minimumPaymentCents,
            0,
        );
        const debtMinBill = intake.bills.find(
            (b) => b.category === 'debt_minimums',
        );
        if (
            debtMinBill != null &&
            debtMinBill.amountCents !== debtMinFromDebts
        ) {
            warnings.push(
                `Debt minimums bill ($${(debtMinBill.amountCents / 100).toLocaleString()}) ≠ sum of debt minimumPaymentCents ($${(debtMinFromDebts / 100).toLocaleString()})`,
            );
        }
    }

    if (intake.incomes != null) {
        if (intake.incomes.monthlyTakeHomeCents !== monthlyTakeHomeCents) {
            warnings.push(
                `incomes.monthlyTakeHomeCents ($${(intake.incomes.monthlyTakeHomeCents / 100).toLocaleString()}) ≠ top-level monthlyTakeHomeCents ($${(monthlyTakeHomeCents / 100).toLocaleString()})`,
            );
        }
        const sourcesSum = intake.incomes.incomeSources.reduce(
            (s, src) => s + src.monthlyCents,
            0,
        );
        if (sourcesSum !== intake.incomes.monthlyTakeHomeCents) {
            warnings.push(
                `Income sources sum ($${(sourcesSum / 100).toLocaleString()}) ≠ incomes.monthlyTakeHomeCents ($${(intake.incomes.monthlyTakeHomeCents / 100).toLocaleString()})`,
            );
        }
    }

    const knownFields = new Set([
        'monthlyTakeHomeCents',
        'monthlyExpensesCents',
        'monthlySpendCents',
        'monthlyGoalsSpendCents',
        'branch',
        'slideText',
        'incomes',
        'bills',
        'debts',
        'debtTypes',
        'savings',
        'investment',
    ]);
    const extraFields = Object.keys(intake).filter((k) => !knownFields.has(k));
    if (extraFields.length > 0) {
        warnings.push(`Unknown fields on intake: ${extraFields.join(', ')}`);
    }

    if (intake.branch == null) {
        warnings.push('Missing expected fields: branch');
    }

    if (!isInvestBranch) {
        const missingCore: string[] = [];
        if (intake.incomes == null) missingCore.push('incomes');
        if (intake.bills == null) missingCore.push('bills');
        if (missingCore.length > 0) {
            warnings.push(`Missing expected fields: ${missingCore.join(', ')}`);
        }
    } else {
        if (intake.investment == null) {
            warnings.push('Missing expected fields: investment');
        } else {
            const waterfall = buildInvestmentWaterfall(intake.investment);
            const hasNaN = waterfall.accounts.some(
                (a) =>
                    Number.isNaN(a.annualAllocationCents) ||
                    Number.isNaN(a.projectedBalanceCents) ||
                    (a.annualLimitCents != null &&
                        Number.isNaN(a.annualLimitCents)),
            );
            if (hasNaN || Number.isNaN(waterfall.projectedTotalCents)) {
                warnings.push('Investment waterfall produced NaN values');
            }
        }
    }

    return warnings;
}

/* ─── Computation via shared computeAhaMoment ─── */

function simulateAhaOutput(intake: AhaMomentIntake): AhaOutput {
    const result = computeAhaMoment(intake);

    const isInvestBranch = intake.branch === CMMBranch.StartInvesting;
    const waterfall =
        isInvestBranch && intake.investment != null
            ? buildInvestmentWaterfall(intake.investment)
            : undefined;

    const debug: SimulationDebug = {
        monthlyGoalsCents: result.moneyMap.goalsTotalCents,
        accounts: buildDebugAccounts(intake, result),
        ruleSets: buildDebugRuleSets(intake, result),
        moneyMapJson: result.simulation
            ? JSON.stringify(result.simulation, null, 2)
            : '',
        waterfall,
    };

    const hasDate = result.date !== '';
    const displayHeadline = computeDisplayHeadline(
        intake,
        result.moneyMap.goalsTotalCents,
        hasDate,
    );

    return {
        intake,
        image: result.image,
        text: result.text,
        date: result.date,
        moneyMap: result.moneyMap,
        simulationDebug: debug,
        slides: result.slides,
        headlineType: result.headlineType,
        displayHeadline,
    };
}

function buildDebugAccounts(
    intake: AhaMomentIntake,
    result: AhaMomentResult,
): SimulationDebug['accounts'] {
    if (!result.simulation) return [];

    const simDebtIds = new Set(
        result.simulation.accounts
            .filter((a) => a.type === 'DEBT')
            .map((a) => a.name),
    );

    const debtAccounts = result.simulation.accounts.filter(
        (a) => a.type === 'DEBT',
    );

    const includedDebts: SimulationDebug['accounts'] = debtAccounts.map(
        (a) => ({
            name: a.name,
            balance: formatCents(a.balanceCents),
            ...('annualInterestRate' in a
                ? {
                      rate: `${(a.annualInterestRate * 100).toFixed(1)}%`,
                  }
                : {}),
            ...('minimumPaymentCents' in a
                ? { minPayment: formatCents(a.minimumPaymentCents) }
                : {}),
        }),
    );

    const allInputDebts = intake?.debts ?? [];
    const excludedDebts: SimulationDebug['accounts'] = allInputDebts
        .filter((d) => !simDebtIds.has(d.description ?? ''))
        .filter((d) => (d.balanceRemainingCents ?? 0) > 0)
        .map((d) => ({
            name: d.description ?? 'Debt',
            balance: formatCents(d.balanceRemainingCents ?? 0),
            rate: `${(d.annualInterestRatePercent ?? 0).toFixed(1)}%`,
            minPayment: formatCents(d.minimumPaymentCents),
            excluded: true,
        }));

    if (includedDebts.length > 0 || excludedDebts.length > 0) {
        return [...includedDebts, ...excludedDebts];
    }

    const savingsGoal = result.simulation.accounts.find(
        (a) => a.id === 'savings-goal',
    );
    if (savingsGoal) {
        return [
            {
                name: savingsGoal.name,
                balance: formatCents(savingsGoal.balanceCents),
            },
        ];
    }

    return [];
}

function buildDebugRuleSets(
    intake: AhaMomentIntake,
    result: AhaMomentResult,
): SimulationDebug['ruleSets'] {
    if (result.simulation != null) {
        return result.simulation.ruleSets.map((rs) => ({
            name: `${rs.name} (priority ${rs.priority})`,
            rules: rs.rules.map((r) => ({
                description: r.description,
                splitPct: `${(r.splitPercentage * 100).toFixed(0)}%`,
                type: r.allocationType,
            })),
        }));
    }

    // Simulation couldn't run (e.g. $0 goals); show static fallback rules
    return buildStaticRuleSetDisplays(intake).map((rs) => ({
        name: `${rs.name} (priority ${rs.priority}) [static]`,
        rules: rs.rules.map((r) => ({
            description: r.description,
            splitPct:
                r.splitPercentage > 0
                    ? `${(r.splitPercentage * 100).toFixed(0)}%`
                    : '',
            type: r.allocationType,
        })),
    }));
}

function formatCents(cents: number): string {
    return `$${(cents / 100).toLocaleString()}`;
}

/* ─── Components ─── */

const BranchChip = ({
    info,
    isSelected,
    onSelect,
}: {
    info: BranchInfo;
    isSelected: boolean;
    onSelect: () => void;
}) => (
    <View
        paddingHorizontal={12}
        paddingVertical={8}
        borderRadius={8}
        borderWidth={1.5}
        borderColor={isSelected ? '$green900' : '$gray200'}
        backgroundColor={isSelected ? '$green300' : '$white'}
        onPress={onSelect}
        cursor="pointer"
        opacity={info.implemented ? 1 : 0.5}
    >
        <FootnoteEmphasized color={isSelected ? '$green900' : '$gray1100'}>
            {info.label}
        </FootnoteEmphasized>
    </View>
);

const AhaOutputPreview = ({ output }: { output: AhaOutput }) => (
    <YStack padding={16} borderRadius={12} backgroundColor="$gray100" gap={12}>
        <LabelEmphasized>Aha Moment Output</LabelEmphasized>
        <XStack gap={16} flexWrap="wrap">
            <YStack gap={2}>
                <LabelRegular color="$gray1000">image</LabelRegular>
                <BodyMediumRegular>{output.image}</BodyMediumRegular>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">headlineType</LabelRegular>
                <BodyMediumRegular>{output.headlineType}</BodyMediumRegular>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">displayHeadline</LabelRegular>
                <BodyMediumEmphasized>
                    {output.displayHeadline}
                    {output.date !== '' ? ` ${output.date}` : ''}
                </BodyMediumEmphasized>
            </YStack>
        </XStack>
        <XStack gap={16} flexWrap="wrap">
            <YStack gap={2}>
                <LabelRegular color="$gray1000">text (raw)</LabelRegular>
                <BodyMediumRegular>
                    {output.text || '(empty)'}
                </BodyMediumRegular>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">date (raw)</LabelRegular>
                <BodyMediumRegular>
                    {output.date || '(empty)'}
                </BodyMediumRegular>
            </YStack>
        </XStack>
        <XStack gap={16} flexWrap="wrap">
            <YStack gap={2}>
                <LabelRegular color="$gray1000">Income</LabelRegular>
                <BodyMediumEmphasized>
                    {formatCents(output.moneyMap.incomeTotalCents)}
                </BodyMediumEmphasized>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">Bills</LabelRegular>
                <BodyMediumEmphasized>
                    {formatCents(output.moneyMap.billsTotalCents)}
                </BodyMediumEmphasized>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">Spend</LabelRegular>
                <BodyMediumEmphasized>
                    {formatCents(output.moneyMap.spendTotalCents)}
                </BodyMediumEmphasized>
            </YStack>
            <YStack gap={2}>
                <LabelRegular color="$gray1000">Goals</LabelRegular>
                <BodyMediumEmphasized>
                    {formatCents(output.moneyMap.goalsTotalCents)}
                </BodyMediumEmphasized>
            </YStack>
        </XStack>

        {output.simulationDebug != null && (
            <YStack
                gap={8}
                padding={12}
                borderRadius={8}
                backgroundColor="$white"
                borderWidth={1}
                borderColor="$gray200"
            >
                <LabelEmphasized>Simulation Inputs</LabelEmphasized>

                {output.simulationDebug.waterfall != null ? (
                    <YStack gap={8}>
                        <XStack gap={16} flexWrap="wrap">
                            <YStack gap={2}>
                                <LabelRegular color="$gray1000">
                                    Annual contribution
                                </LabelRegular>
                                <BodyMediumEmphasized>
                                    {formatCents(
                                        output.simulationDebug.waterfall
                                            .totalAnnualContributionCents,
                                    )}
                                </BodyMediumEmphasized>
                            </YStack>
                            <YStack gap={2}>
                                <LabelRegular color="$gray1000">
                                    Starting balance
                                </LabelRegular>
                                <BodyMediumEmphasized>
                                    {formatCents(
                                        output.simulationDebug.waterfall
                                            .startingBalanceCents +
                                            output.simulationDebug.waterfall
                                                .plannedStartingCashContributionCents,
                                    )}
                                </BodyMediumEmphasized>
                            </YStack>
                            <YStack gap={2}>
                                <LabelRegular color="$gray1000">
                                    Projected total
                                </LabelRegular>
                                <BodyMediumEmphasized>
                                    {formatCents(
                                        output.simulationDebug.waterfall
                                            .projectedTotalCents,
                                    )}
                                </BodyMediumEmphasized>
                            </YStack>
                            <YStack gap={2}>
                                <LabelRegular color="$gray1000">
                                    Return / Years
                                </LabelRegular>
                                <BodyMediumEmphasized>
                                    {
                                        output.simulationDebug.waterfall
                                            .annualReturnPercent
                                    }
                                    % /{' '}
                                    {
                                        output.simulationDebug.waterfall
                                            .projectedYears
                                    }{' '}
                                    yrs
                                </BodyMediumEmphasized>
                            </YStack>
                        </XStack>
                        <YStack gap={4}>
                            <LabelRegular color="$gray1000">
                                Waterfall accounts
                            </LabelRegular>
                            {output.simulationDebug.waterfall.accounts.map(
                                (acct) => (
                                    <YStack key={acct.id} gap={2}>
                                        <BodySmallRegular color="$gray1100">
                                            {acct.label} ({acct.memberLabel}) —{' '}
                                            {acct.accountType},{' '}
                                            {acct.rothEligibility}
                                        </BodySmallRegular>
                                        <BodySmallRegular color="$gray1100">
                                            {'  '}Allocation:{' '}
                                            {formatCents(
                                                acct.annualAllocationCents,
                                            )}
                                            /yr
                                            {acct.annualLimitCents != null
                                                ? ` (limit ${formatCents(acct.annualLimitCents)})`
                                                : ' (no limit)'}
                                            {' · '}Projected:{' '}
                                            {formatCents(
                                                acct.projectedBalanceCents,
                                            )}
                                        </BodySmallRegular>
                                        {acct.notes.map((note, i) => (
                                            <BodySmallRegular
                                                key={i}
                                                color="$orange1000"
                                            >
                                                {'  '}⚠ {note}
                                            </BodySmallRegular>
                                        ))}
                                    </YStack>
                                ),
                            )}
                        </YStack>
                        <YStack gap={4}>
                            <LabelRegular color="$gray1000">
                                Roth eligibility
                            </LabelRegular>
                            {(() => {
                                const rd =
                                    output.simulationDebug.waterfall
                                        .rothDetails;
                                return (
                                    <YStack gap={2}>
                                        <BodySmallRegular color="$gray1100">
                                            Filing status: {rd.filingStatus}
                                        </BodySmallRegular>
                                        <BodySmallRegular color="$gray1100">
                                            Income (MAGI):{' '}
                                            {formatCents(rd.incomeCents)}
                                        </BodySmallRegular>
                                        <BodySmallRegular color="$gray1100">
                                            Phase-out start:{' '}
                                            {formatCents(rd.phaseOutStartCents)}
                                        </BodySmallRegular>
                                        <BodySmallRegular
                                            color={
                                                rd.useBackdoor
                                                    ? '$orange1000'
                                                    : '$green1000'
                                            }
                                        >
                                            →{' '}
                                            {rd.useBackdoor
                                                ? 'Backdoor Roth recommended'
                                                : 'Direct Roth eligible'}
                                        </BodySmallRegular>
                                    </YStack>
                                );
                            })()}
                        </YStack>
                        <YStack gap={4}>
                            <LabelRegular color="$gray1000">
                                Fee comparison (
                                {
                                    output.simulationDebug.waterfall
                                        .feeComparison.years
                                }
                                yr @{' '}
                                {
                                    output.simulationDebug.waterfall
                                        .feeComparison.assumedReturnPercent
                                }
                                % return)
                            </LabelRegular>
                            {output.simulationDebug.waterfall.feeComparison.rows.map(
                                (row) => (
                                    <BodySmallRegular
                                        key={row.provider}
                                        color="$gray1100"
                                    >
                                        {row.provider} (
                                        {row.feePercent.toFixed(2)}%):{' '}
                                        {formatCents(row.totalFeesCents)} in
                                        fees
                                    </BodySmallRegular>
                                ),
                            )}
                        </YStack>
                    </YStack>
                ) : (
                    <>
                        <XStack gap={16} flexWrap="wrap">
                            <YStack gap={2}>
                                <LabelRegular color="$gray1000">
                                    Monthly to goals
                                </LabelRegular>
                                <BodyMediumEmphasized>
                                    {formatCents(
                                        output.simulationDebug
                                            .monthlyGoalsCents,
                                    )}
                                </BodyMediumEmphasized>
                            </YStack>
                        </XStack>
                        {output.simulationDebug.accounts.length > 0 && (
                            <YStack gap={4}>
                                <LabelRegular color="$gray1000">
                                    Accounts in simulation
                                </LabelRegular>
                                {output.simulationDebug.accounts.map(
                                    (acct, i) => (
                                        <BodySmallRegular
                                            key={i}
                                            color={
                                                acct.excluded
                                                    ? '$orange1000'
                                                    : '$gray1100'
                                            }
                                        >
                                            {acct.excluded ? '⊘ ' : ''}
                                            {acct.name}: {acct.balance}
                                            {acct.rate != null
                                                ? ` @ ${acct.rate}`
                                                : ''}
                                            {acct.minPayment != null
                                                ? ` (min ${acct.minPayment}/mo)`
                                                : ''}
                                            {acct.excluded
                                                ? ' — excluded (≤10% APR)'
                                                : ''}
                                        </BodySmallRegular>
                                    ),
                                )}
                            </YStack>
                        )}
                        {output.simulationDebug.ruleSets.length > 0 && (
                            <YStack gap={4}>
                                <LabelRegular color="$gray1000">
                                    Rule Sets
                                </LabelRegular>
                                {output.simulationDebug.ruleSets.map(
                                    (rs, i) => (
                                        <YStack key={i} gap={2}>
                                            <BodySmallRegular color="$gray1100">
                                                {rs.name}
                                            </BodySmallRegular>
                                            {rs.rules.map((rule, j) => (
                                                <BodySmallRegular
                                                    key={j}
                                                    color="$gray1000"
                                                    paddingLeft={12}
                                                >
                                                    {rule.splitPct} →{' '}
                                                    {rule.description} (
                                                    {rule.type})
                                                </BodySmallRegular>
                                            ))}
                                        </YStack>
                                    ),
                                )}
                            </YStack>
                        )}
                        {output.simulationDebug.moneyMapJson.length > 0 && (
                            <YStack gap={8}>
                                <LabelEmphasized>
                                    MoneyMapSimulation JSON
                                </LabelEmphasized>
                                <BodySmallRegular
                                    color="$gray1100"
                                    fontFamily={Platform.select({
                                        ios: 'Menlo',
                                        android: 'monospace',
                                        web: 'monospace',
                                    })}
                                >
                                    {output.simulationDebug.moneyMapJson}
                                </BodySmallRegular>
                                <Button
                                    text="View in Simulator"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    onPress={() => {
                                        const url = `http://localhost:8081/dev/money-map/money-map-simulator?moneyMap=${encodeURIComponent(output.simulationDebug?.moneyMapJson ?? '')}`;
                                        void Linking.openURL(url);
                                    }}
                                />
                            </YStack>
                        )}
                    </>
                )}
            </YStack>
        )}
    </YStack>
);

/* ─── Main ─── */

const PlaygroundPage = () => {
    const setOnboardingChatResults = useSetAtom(onboardingChatResultsAtom);
    const [selectedBranch, setSelectedBranch] = useState<CMMBranch>(
        CMMBranch.GoalsDebt,
    );
    const [jsonText, setJsonText] = useState(
        JSON.stringify(BRANCH_PRESETS[CMMBranch.GoalsDebt], null, 2),
    );
    const [parseError, setParseError] = useState<string | null>(null);

    const selectBranch = useCallback((branch: CMMBranch) => {
        setSelectedBranch(branch);
        const preset = BRANCH_PRESETS[branch];
        if (preset != null) {
            setJsonText(JSON.stringify(preset, null, 2));
            setParseError(null);
        }
    }, []);

    const parsedIntake = (() => {
        try {
            const raw = JSON.parse(jsonText) as AhaMomentIntake;
            if (raw.investment != null) {
                raw.investment = initIntakeInvestment(raw.investment);
            }
            return raw;
        } catch {
            return null;
        }
    })();

    const intakeWarnings =
        parsedIntake != null ? validateIntakeConsistency(parsedIntake) : [];

    const ahaOutput =
        parsedIntake != null ? simulateAhaOutput(parsedIntake) : null;

    const handlePreview = useCallback(() => {
        if (ahaOutput == null || parsedIntake == null) {
            setParseError('Fix JSON errors before previewing');
            return;
        }
        setParseError(null);

        setOnboardingChatResults({
            moneyMap: {
                incomeTotalCents: ahaOutput.moneyMap.incomeTotalCents,
                billsTotalCents: ahaOutput.moneyMap.billsTotalCents,
                spendTotalCents: ahaOutput.moneyMap.spendTotalCents,
                goalsTotalCents: ahaOutput.moneyMap.goalsTotalCents,
            },
            artifactData: {
                debtFreeDate: ahaOutput.date,
                image: ahaOutput.image,
                slides: ahaOutput.slides.join(','),
            },
            rawChatResults: parsedIntake,
        });

        storage.removeItem(
            StorageKeys.HAS_SEEN_CONVERSATIONAL_MM_GUIDED_TOOLTIPS,
        );
        router.push('/sign-up/get-started/results');
    }, [ahaOutput, parsedIntake, setOnboardingChatResults]);

    // Use process.env directly (not IS_PRODUCTION) so the bundler can
    // dead-code-eliminate this component from production builds.
    if (
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'local' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'sandbox' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'staging'
    ) {
        return <Redirect href="/" />;
    }

    return (
        <ScreenContainer
            title="Aha Moment Playground"
            hasBackButton
            onFallbackRoute="/dev"
            maxWidthOverride={960}
        >
            <YStack gap={24} paddingBottom={40}>
                {/* ── Branches ── */}
                <YStack
                    gap={12}
                    padding={16}
                    borderRadius={12}
                    backgroundColor="$gray200"
                >
                    <H5Emphasized>Branches</H5Emphasized>
                    <BodySmallRegular color="$gray1000">
                        Tap a preset to load sample intake data. Grouped by
                        welcome question.
                    </BodySmallRegular>
                    {BRANCH_GROUPS.map((group) => (
                        <YStack key={group.welcomeOption} gap={6}>
                            <LabelRegular color="$gray1000">
                                {group.welcomeOption}
                            </LabelRegular>
                            <XStack gap={8} flexWrap="wrap">
                                {group.branches.map((info) => (
                                    <BranchChip
                                        key={info.branch}
                                        info={info}
                                        isSelected={
                                            selectedBranch === info.branch
                                        }
                                        onSelect={() => {
                                            selectBranch(info.branch);
                                        }}
                                    />
                                ))}
                            </XStack>
                        </YStack>
                    ))}
                </YStack>

                {/* ── JSON editor ── */}
                <YStack gap={8}>
                    <XStack justifyContent="space-between" alignItems="center">
                        <H5Emphasized>ConversationalMMIntake JSON</H5Emphasized>
                        <BodySmallRegular color="$gray1000">
                            Chat-captured intake (slider defaults applied on
                            preview)
                        </BodySmallRegular>
                    </XStack>

                    <TextInput
                        value={jsonText}
                        onChangeText={(text) => {
                            setJsonText(text);
                            setParseError(null);
                        }}
                        multiline
                        autoCapitalize="none"
                        autoCorrect={false}
                        spellCheck={false}
                        style={{
                            fontFamily: Platform.select({
                                ios: 'Menlo',
                                android: 'monospace',
                                web: 'monospace',
                            }),
                            fontSize: 13,
                            lineHeight: 20,
                            backgroundColor: '#1e1e2e',
                            color: '#cdd6f4',
                            borderRadius: 12,
                            padding: 16,
                            minHeight: 480,
                            textAlignVertical: 'top',
                            ...(Platform.OS === 'web'
                                ? { resize: 'vertical' }
                                : {}),
                        }}
                    />

                    {parseError != null && (
                        <View
                            backgroundColor="$red200"
                            padding={12}
                            borderRadius={8}
                        >
                            <BodyMediumEmphasized color="$red1100">
                                Parse error
                            </BodyMediumEmphasized>
                            <BodySmallRegular color="$red1000">
                                {parseError}
                            </BodySmallRegular>
                        </View>
                    )}
                </YStack>

                {/* ── Consistency warnings ── */}
                {intakeWarnings.length > 0 && (
                    <YStack
                        padding={12}
                        borderRadius={8}
                        backgroundColor="$orange300"
                        gap={4}
                    >
                        <LabelEmphasized color="$orange1000">
                            Intake consistency warnings ({intakeWarnings.length}
                            )
                        </LabelEmphasized>
                        {intakeWarnings.map((w, i) => (
                            <BodySmallRegular key={i} color="$orange1100">
                                • {w}
                            </BodySmallRegular>
                        ))}
                    </YStack>
                )}

                {/* ── Aha output preview ── */}
                {ahaOutput != null && <AhaOutputPreview output={ahaOutput} />}

                <Button
                    text="Preview Results Carousel"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={handlePreview}
                    width="full"
                />
            </YStack>
        </ScreenContainer>
    );
};

export default PlaygroundPage;
